import React from "react";
import AddIcon from "@material-ui/icons/Add";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import Grid from "@material-ui/core/Grid";
import Toolbar from "@material-ui/core/Toolbar";
import Tooltip from "@material-ui/core/Tooltip";
import withStyles from "@material-ui/core/styles/withStyles";
import { getIn, FastField, Field, FieldArray } from "formik";
import PropertySection from "../property/PropertySection";
import memoAssign from "../../util/memoAssign";
import { AddPropertyFieldComponent } from "../property/PropertyField";

const addRowToolbarStyles = theme => ({
  leftIcon: { marginRight: theme.spacing(1) }
});

const AddRowButton = withStyles(addRowToolbarStyles, {
  name: "LcListFieldAddRowButton"
})(({ classes, onClick, label, FieldAddIcon, variant, disabled, tooltip }) =>
  tooltip ? (
    <Toolbar variant="dense" disableGutters>
      <Tooltip title={tooltip}>
        <div>
          <Button variant={variant} onClick={onClick} disabled={disabled}>
            <FieldAddIcon fontSize="small" className={classes.leftIcon} />
            {label}
          </Button>
        </div>
      </Tooltip>
    </Toolbar>
  ) : (
    <Toolbar variant="dense" disableGutters>
      <Button variant={variant} onClick={onClick} disabled={disabled}>
        <FieldAddIcon fontSize="small" className={classes.leftIcon} />
        {label}
      </Button>
    </Toolbar>
  )
);

const deleteRowToolbarStyles = theme => ({
  leftIcon: {
    marginRight: theme.spacing(1)
  }
});

const DeleteRowButton = withStyles(deleteRowToolbarStyles, {
  name: "LcListFieldDeleteRowButton"
})(({ classes, onClick, label, FieldDeleteIcon, variant, disabled }) => (
  <Toolbar variant="dense" disableGutters>
    <Button variant={variant} onClick={onClick} disabled={disabled}>
      <FieldDeleteIcon fontSize="small" className={classes.leftIcon} />
      {label}
    </Button>
  </Toolbar>
));

export const ListField = withStyles({}, { name: "LcListField" })(
  function ListField({
    classes,
    name,
    fieldInfo = {},
    sections = [],
    add: {
      icon: FieldAddIcon = AddIcon,
      label: addLabel = "Add",
      tooltip: addTooltip
    } = {},
    delete: {
      icon: FieldDeleteIcon = DeleteForeverIcon,
      label: deleteLabel = "Delete"
    } = {},
    min = -1,
    max = -1,
    header = { paddingBottom: 1 },
    section = {
      marginBottom: 2,
      marginRight: 2,
      border: 1,
      borderColor: "#ced4da",
      paddingLeft: 2,
      paddingBottom: 1,
      paddingTop: 3,
      borderRadius: "2px"
    },
    field = {
      paddingBottom: 2,
      paddingRight: 2
    },
    readOnly = false,
    defaultValues = {},
    disabled,
    direction = "row",
    title,
    titleBox = {},
    fast = true,
    FieldComponent = fast ? FastField : Field,
    flexGrow = 0
  }) {
    return (
      <FieldComponent name={name}>
        {({ field: { value }, form }) => {
          const fieldTouched =
            form.submitCount > 0 || getIn(form.touched, name);
          const error = getIn(form.errors, name);
          const hasError = Boolean(fieldTouched && error);

          const dis = Boolean(
            form.isSubmitting || form.isValidating || disabled
          );

          const items = value || [];

          return (
            <FieldArray
              disableFast
              name={name}
              render={arrayHelpers => (
                <>
                  <Grid
                    container
                    classes={{ root: classes.gridRoot }}
                    direction={direction}
                  >
                    {title && (
                      <Grid item xs={12} classes={{ root: classes.gridTitle }}>
                        <Box {...titleBox}>{title}</Box>
                      </Grid>
                    )}
                    {items.map((item, idx) => {
                      const sectionComponents =
                        sections &&
                        sections.map(
                          ({
                            hidden,
                            xs,
                            sm,
                            md,
                            lg,
                            xl,
                            box = {},
                            field: sectionField = {},
                            header: sectionHeader = {},
                            ...sectionEntry
                          }) => {
                            const shouldHide =
                              hidden !== undefined && hidden(item, form);

                            if (shouldHide) return null;

                            const key = name + "[" + idx + "]";

                            return (
                              <Grid
                                item
                                key={key}
                                xs={xs}
                                sm={sm}
                                md={md}
                                lg={lg}
                                xl={xl}
                                style={{ flexGrow }}
                              >
                                <PropertySection
                                  section={{
                                    ...(section || {}),
                                    ...(box || {})
                                  }}
                                  disabled={dis}
                                  shouldHide={shouldHide}
                                  fieldInfo={fieldInfo}
                                  {...sectionEntry}
                                  header={memoAssign(header, sectionHeader)}
                                  field={memoAssign(field, sectionField)}
                                  prefix={key}
                                >
                                  {!readOnly && items.length > min && (
                                    <>
                                      <DeleteRowButton
                                        disabled={dis}
                                        onClick={() => {
                                          form.setFieldError(name, undefined);
                                          arrayHelpers.remove(idx);
                                        }}
                                        label={deleteLabel}
                                        FieldDeleteIcon={FieldDeleteIcon}
                                      />
                                    </>
                                  )}
                                </PropertySection>
                              </Grid>
                            );
                          }
                        );

                      return sectionComponents;
                    })}
                  </Grid>
                  {!readOnly && (max === -1 || max > items.length) && (
                    <AddRowButton
                      disabled={dis}
                      onClick={() => {
                        arrayHelpers.push(defaultValues);
                      }}
                      label={addLabel}
                      FieldAddIcon={FieldAddIcon}
                      tooltip={addTooltip}
                    />
                  )}
                  {hasError && (
                    <FormControl error={hasError}>
                      {hasError && typeof error === "string" && (
                        <FormHelperText>{error}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                </>
              )}
            />
          );
        }}
      </FieldComponent>
    );
  }
);

AddPropertyFieldComponent("list", ListField);

export default ListField;
